import React, { useState } from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Box, Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'root/apps/helpers/react-renderer';

import { StyledModal } from './Modal.style';

interface OrderedStatusNuOrderModalProps {
  callback?: (result: boolean) => void;
  unmount?: () => void;
}

const OrderedStatusNuOrderModal: React.FC<OrderedStatusNuOrderModalProps> = ({
  callback,
  unmount,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isModalSuppressed, setIsModalSuppressed] = useState(false);

  const { id: orderId } = window.history.getFilters();

  const savePreference = () => {
    const value = isModalSuppressed ? 'hide' : 'show';
    window.lsCloud.UserPreferences.save(
      'view',
      'purchase.views.purchase',
      'send_to_nuorder',
      value
    );
  };

  const handleConfirm = () => {
    setIsOpen(false);
    savePreference();
    window.clearFieldChanges('view');
    if (typeof callback === 'function') callback(false);
    if (typeof unmount === 'function') unmount();
    window.navigateToContent('view', 'purchase.views.purchase', orderId, 'nuorder_ordering');
  };

  const handleDontSend = () => {
    savePreference();
    setIsOpen(false);
    if (typeof callback === 'function') callback(true);
    if (typeof unmount === 'function') unmount();
  };

  const handleDismiss = () => {
    setIsOpen(false);
    if (typeof callback === 'function') callback(false);
    if (typeof unmount === 'function') unmount();
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={() => handleDismiss()}>
      <ModalHeader data-test="ordered-status-modal">
        Send linked PO to NuORDER and submit working order?
      </ModalHeader>
      <ModalBody>
        <Text fontSize={14}>
          The vendor for the PO that you are sending is linked to NuORDER. If your PO is ready, you
          can send it to NuORDER now to create and submit a working order to vendor.
        </Text>
        <Text mt={2} fontSize={14}>
          You can proceed without sending the PO to NuORDER and choose to send it later.
        </Text>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="space-between">
          <Box>
            <Checkbox
              checked={isModalSuppressed}
              onChange={(event) => setIsModalSuppressed(event.target.checked)}
              label="Don’t show this message again."
              data-test="ordered-status-modal-do-not-show-again-checkbox"
            />
          </Box>
          <Box>
            <Button data-test="ordered-status-modal-do-not-send-btn" onClick={handleDontSend}>
              Don&apos;t send and mark as ordered
            </Button>
            <Button
              data-test="ordered-status-modal-send-to-nuorder"
              fill
              variant="secondary"
              ml={2}
              onClick={handleConfirm}
            >
              Send to NuORDER
            </Button>
          </Box>
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
};

export default OrderedStatusNuOrderModal;

export const setOrderedStatusNuOrderModalBypass = (modalProps: OrderedStatusNuOrderModalProps) => {
  const RenderedComponent = createReactRenderer({
    component: OrderedStatusNuOrderModal,
    selector: '[data-react-root="ReactModal"]',
  });

  const props = {
    ...modalProps,
    unmount: RenderedComponent.unmount,
  };

  RenderedComponent.mount({ props });

  // Do NOT auto-remount on tab change anymore
  history.onChangeState(
    [
      {
        name: ['purchase.views.purchase'],
        form_name: 'view',
        tab: 'main',
      },
    ],
    () => {
      // Don't auto-mount — asyncHook should explicitly trigger it
    },
    () => {
      RenderedComponent.unmount();
    }
  );
};
